import React, { Fragment, useCallback, useEffect, useState, useMemo } from "react";
import {
    Container,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from "reactstrap";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import OrderDetailModal from "../common/OrderDetailModal";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Badge, EventBadge, Loader } from "../reporting/ReportingShared";
import { getLoyaltyLedger, getOrderListing, getUserListing } from "../../utils/apiCalls";

const inputStyle = {
    padding: "10px 14px", borderRadius: 10, fontSize: 13,
    border: `1px solid ${C.border}`, background: C.white,
    color: C.text, outline: "none", width: "100%",
};
const btnPrimary = {
    padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700,
    border: "none", cursor: "pointer", color: C.white,
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
};
const btnSecondary = {
    padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 600,
    border: `1px solid ${C.border}`, cursor: "pointer", color: C.textMed, background: C.white,
};

const eventTypes = [
    "earn_points", "revoke_points", "redeem_points", "redeem_points_reversal",
    "earn_stamp", "revoke_stamp", "redeem_stamp", "redeem_stamp_reversal",
    "use_reward", "use_reward_reversal", "reward_expired", "reward_refunded",
    "manual_stamp_adjust", "manual_reward_adjust", "manual_points_adjust",
    "import_opening_balance",
];

/**
 * A manual reward adjustment moves neither delta column, and an imported opening balance carries
 * its rewards beside the stamps; in both the reward count is in meta.
 */
const REWARDS_IN_META = ["manual_reward_adjust", "import_opening_balance"];
const rewardsDeltaOf = (item) => {
    if (!REWARDS_IN_META.includes(item?.eventType)) return 0;
    let meta = item.meta;
    if (typeof meta === "string") {
        try { meta = JSON.parse(meta); } catch (e) { meta = null; }
    }
    return Number(meta?.rewardsDelta) || 0;
};

/** Every non-zero movement on a row — an imported opening balance can carry stamps AND rewards. */
const deltaParts = (item) =>
    [
        { n: Number(item?.pointsDelta) || 0, unit: "pts" },
        { n: Number(item?.stampsDelta) || 0, unit: "stamps" },
        { n: rewardsDeltaOf(item), unit: "rewards" },
    ].filter((p) => p.n !== 0);

const eventTypeConfig = {
    earn_points: { label: "Points Earned", description: "Customer earned loyalty points from a purchase" },
    revoke_points: { label: "Points Revoked", description: "Loyalty points were revoked or removed from customer account" },
    redeem_points: { label: "Points Redeemed", description: "Customer redeemed accumulated points for a reward" },
    redeem_points_reversal: { label: "Points Redemption Reversed", description: "Points redemption was reversed and points restored" },
    earn_stamp: { label: "Stamp Earned", description: "Customer earned a loyalty stamp from a purchase" },
    revoke_stamp: { label: "Stamp Revoked", description: "A loyalty stamp was revoked or removed from customer account" },
    redeem_stamp: { label: "Stamp Redeemed", description: "Customer redeemed accumulated stamps for a reward" },
    redeem_stamp_reversal: { label: "Stamp Redemption Reversed", description: "Stamp redemption was reversed and stamps restored" },
    use_reward: { label: "Reward Used", description: "Customer used a reward (voucher or direct redemption)" },
    use_reward_reversal: { label: "Reward Usage Reversed", description: "Reward usage was reversed and reward restored" },
    reward_expired: { label: "Reward Expired", description: "Reward voucher expired and was forfeited" },
    reward_refunded: { label: "Reward Refunded", description: "Reward voucher expired and reward was refunded to customer" },
    manual_stamp_adjust: { label: "Manual Stamp Adjustment", description: "Stamps added or deducted by staff from the admin panel" },
    manual_reward_adjust: { label: "Manual Reward Adjustment", description: "Rewards added or deducted by staff from the admin panel" },
    manual_points_adjust: { label: "Manual Points Adjustment", description: "Points added or deducted by staff from the admin panel" },
    import_opening_balance: { label: "Opening Balance (Import)", description: "Balance carried over from the merchant's previous loyalty system by a customer import" },
};

const getEventTypeData = (et) => eventTypeConfig[et] || { label: et || "N/A", description: "No description available" };

const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
        + ", " + date.toLocaleTimeString("en-US", { hour: "numeric", minute: "numeric", hour12: true });
};

const LoyaltyLedger = () => {
    const userType = localStorage.getItem("userType");
    const loggedInMerchantId = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const selectedMerchant = userType === "merchant" ? loggedInMerchantId : reduxSelectedMerchant;

    const [isLoading, setIsLoading] = useState(false);
    const [ledgerData, setLedgerData] = useState([]);
    const [users, setUsers] = useState([]);
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [orderModalOpen, setOrderModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [loyaltyModalOpen, setLoyaltyModalOpen] = useState(false);
    const [selectedLoyalty, setSelectedLoyalty] = useState(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [limit, setLimit] = useState(10);
    const [tempLimit, setTempLimit] = useState(10);

    // Filters
    const initialFilters = {
        merchantId: selectedMerchant || "",
        userId: "", orderId: "", eventType: "",
        startDate: "", endDate: "",
    };
    const [filters, setFilters] = useState(initialFilters);
    const [tempFilters, setTempFilters] = useState(initialFilters);

    useEffect(() => {
        (async () => {
            const userList = await getUserListing({ limit: 1000 });
            const usersArray = userList?.users || [];
            setUsers(usersArray.filter((u) => u.userType === "Customer"));
        })();
    }, [userType]);

    const fetchData = useCallback(async (page = 1) => {
        setIsLoading(true);
        const response = await getLoyaltyLedger(page, limit, filters);
        if (response && response.data) {
            setLedgerData(response.data.entries || []);
            setTotalPages(response.data.totalPages || 1);
        } else {
            setLedgerData([]);
            setTotalPages(1);
        }
        setIsLoading(false);
    }, [filters, limit]);

    useEffect(() => { fetchData(currentPage); }, [fetchData, currentPage]);

    useEffect(() => {
        setFilters((prev) => ({ ...prev, merchantId: selectedMerchant || "" }));
        setTempFilters((prev) => ({ ...prev, merchantId: selectedMerchant || "" }));
    }, [selectedMerchant]);

    useEffect(() => {
        if (filterModalOpen) { setTempFilters(filters); setTempLimit(limit); }
    }, [filterModalOpen, filters, limit]);

    const handlePageChange = (page) => {
        if (page > 0 && page <= totalPages) setCurrentPage(page);
    };

    const applyFilters = () => {
        setFilters(tempFilters); setLimit(tempLimit); setCurrentPage(1); setFilterModalOpen(false);
    };
    const clearFilters = () => {
        const reset = { ...initialFilters, merchantId: selectedMerchant || "" };
        setTempFilters(reset); setFilters(reset); setTempLimit(10); setLimit(10);
        setCurrentPage(1); setFilterModalOpen(false);
    };

    const handleOrderClick = async (orderId) => {
        const ResData = await getOrderListing(1, 10, { orderId });
        if (ResData?.data?.orders?.length > 0) {
            setSelectedOrder(ResData.data.orders[0]);
            setOrderModalOpen(true);
        }
    };
    const handleLoyaltyClick = (loyalty) => {
        if (loyalty) { setSelectedLoyalty(loyalty); setLoyaltyModalOpen(true); }
    };

    const tiles = useMemo(() => {
        const earned = ledgerData.filter((e) => e.eventType === "earn_points" || e.eventType === "earn_stamp").length;
        const redeemed = ledgerData.filter((e) => ["redeem_points", "redeem_stamp", "use_reward"].includes(e.eventType)).length;
        const reversals = ledgerData.filter((e) => e.eventType?.includes("revers") || e.eventType === "reward_expired" || e.eventType === "reward_refunded" || e.eventType === "revoke_points" || e.eventType === "revoke_stamp").length;
        return { earned, redeemed, reversals };
    }, [ledgerData]);

    const activeFilterCount = useMemo(() => {
        let n = 0;
        if (filters.userId) n++; if (filters.orderId) n++; if (filters.eventType) n++;
        if (filters.startDate) n++; if (filters.endDate) n++;
        return n;
    }, [filters]);

    // Pagination renderer
    const renderPagination = () => {
        const pages = [];
        const maxPagesToShow = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
        for (let i = startPage; i <= endPage; i++) pages.push(i);
        return pages;
    };
    const pageBtnStyle = (active, disabled) => ({
        minWidth: 36, height: 36, padding: "0 12px", borderRadius: 8,
        border: `1px solid ${active ? C.coral : C.border}`,
        background: active ? `linear-gradient(135deg, ${C.coral}, #00bfa5)` : C.white,
        color: active ? C.white : C.textMed,
        fontSize: 13, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1, transition: "all 0.15s",
    });

    return (
        <Fragment>
            <Breadcrumb title="Loyalty Ledger" parent="Loyalty" />
            <Container fluid>
                <style>{reportingGlobalCSS}</style>
                <div className="rpt-wrap">

                    {/* Tiles */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 20 }}>
                        <Tile label="Total Entries" value={ledgerData.length} sub={`Page ${currentPage} of ${totalPages}`} icon="📒" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
                        <Tile label="Earn Events" value={tiles.earned} sub="Stamps / points earned" icon="⭐" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={80} />
                        <Tile label="Redeem Events" value={tiles.redeemed} sub="Stamps / rewards used" icon="🎁" gradient={`linear-gradient(135deg, ${C.orange}, #FFB347)`} delay={160} />
                        <Tile label="Adjustments" value={tiles.reversals} sub="Reversals, expirations, refunds" icon="🔄" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={240} />
                    </div>

                    {/* Table */}
                    <RCard
                        title={
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                <span>📒 Ledger <span style={{ color: C.textLight, fontWeight: 500, marginLeft: 8 }}>· {ledgerData.length} on this page</span></span>
                                <button onClick={() => setFilterModalOpen(true)} style={btnPrimary}>
                                    Filters {activeFilterCount > 0 && <span style={{ background: "rgba(255,255,255,0.25)", padding: "2px 8px", borderRadius: 10, marginLeft: 6, fontSize: 11 }}>{activeFilterCount}</span>}
                                </button>
                            </div>
                        }
                    >
                        {isLoading ? (
                            <Loader />
                        ) : ledgerData.length === 0 ? (
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>No ledger entries found.</div>
                        ) : (
                            <>
                                <div style={{ overflowX: "auto" }}>
                                    <table style={tbl.table}>
                                        <thead>
                                            <tr>
                                                <th style={tbl.th}>ID</th>
                                                <th style={tbl.th}>Date</th>
                                                <th style={tbl.th}>User</th>
                                                <th style={tbl.th}>Merchant</th>
                                                <th style={tbl.th}>Order</th>
                                                <th style={tbl.th}>Event</th>
                                                <th style={tbl.th}>Delta</th>
                                                <th style={tbl.th}>Loyalty</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ledgerData.map((item) => {
                                                const event = getEventTypeData(item?.eventType);
                                                return (
                                                    <tr key={item.id} className="rpt-row">
                                                        <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>#{item.id}</td>
                                                        <td style={{ ...tbl.td, color: C.textMed, fontSize: 12, whiteSpace: "nowrap" }}>{formatDate(item.createdAt)}</td>
                                                        <td style={{ ...tbl.td, fontWeight: 600, color: C.text }}>{item.user ? `${item.user.firstName || ""} ${item.user.lastName || ""}`.trim() || "—" : "—"}</td>
                                                        <td style={{ ...tbl.td, color: C.textMed }}>{item.merchant?.name || "—"}</td>
                                                        <td style={tbl.td}>
                                                            {item.orderId ? (
                                                                <span
                                                                    style={{ cursor: "pointer", color: C.coral, fontWeight: 700, fontSize: 12 }}
                                                                    onClick={() => handleOrderClick(item.orderId)}
                                                                    title="Click to view order"
                                                                >
                                                                    #{item.orderId}
                                                                </span>
                                                            ) : <span style={{ color: C.textLight }}>—</span>}
                                                        </td>
                                                        <td style={tbl.td}>
                                                            <span title={event.description}>
                                                                <EventBadge t={event.label} />
                                                            </span>
                                                        </td>
                                                        <td style={tbl.td}>
                                                            {deltaParts(item).length ? (
                                                                <span style={{ fontWeight: 700, color: deltaParts(item).some((p) => p.n < 0) ? C.coral : C.green }}>
                                                                    {deltaParts(item).map((p) => `${p.n > 0 ? "+" : ""}${p.n} ${p.unit}`).join(", ")}
                                                                </span>
                                                            ) : <span style={{ color: C.textLight }}>0</span>}
                                                        </td>
                                                        <td style={tbl.td}>
                                                            {item.merchantLoyalty ? (
                                                                <span
                                                                    style={{ cursor: "pointer", color: C.coral, fontWeight: 600, fontSize: 12 }}
                                                                    onClick={() => handleLoyaltyClick(item.merchantLoyalty)}
                                                                >
                                                                    {item.merchantLoyalty.name || `#${item.merchantLoyaltyId}`}
                                                                </span>
                                                            ) : <span style={{ color: C.textLight }}>—</span>}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {totalPages > 1 && (
                                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginTop: 24 }}>
                                        <button style={pageBtnStyle(false, currentPage <= 1)} disabled={currentPage <= 1} onClick={() => handlePageChange(currentPage - 1)}>‹</button>
                                        {renderPagination().map((p) => (
                                            <button key={p} style={pageBtnStyle(p === currentPage, false)} onClick={() => handlePageChange(p)}>{p}</button>
                                        ))}
                                        <button style={pageBtnStyle(false, currentPage >= totalPages)} disabled={currentPage >= totalPages} onClick={() => handlePageChange(currentPage + 1)}>›</button>
                                    </div>
                                )}
                            </>
                        )}
                    </RCard>
                </div>
            </Container>

            {/* Filter Modal */}
            <Modal isOpen={filterModalOpen} toggle={() => setFilterModalOpen(false)}>
                <ModalHeader toggle={() => setFilterModalOpen(false)}>Filter Ledger</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>PER PAGE</Label>
                            <Input type="select" style={inputStyle} value={tempLimit} onChange={(e) => setTempLimit(parseInt(e.target.value))}>
                                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>USER</Label>
                            <Input type="select" style={inputStyle} value={tempFilters.userId} onChange={(e) => setTempFilters({ ...tempFilters, userId: e.target.value })}>
                                <option value="">All Users</option>
                                {users.map((u) => (
                                    <option key={u.id} value={u.id}>
                                        {u.firstName} {u.lastName} ({u.phoneNo})
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>EVENT TYPE</Label>
                            <Input type="select" style={inputStyle} value={tempFilters.eventType} onChange={(e) => setTempFilters({ ...tempFilters, eventType: e.target.value })}>
                                <option value="">All Events</option>
                                {eventTypes.map((t) => (
                                    <option key={t} value={t}>{t.replace(/_/g, " ").toUpperCase()}</option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>ORDER ID</Label>
                            <Input type="text" style={inputStyle} value={tempFilters.orderId} onChange={(e) => setTempFilters({ ...tempFilters, orderId: e.target.value })} placeholder="Enter Order ID" />
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>START DATE</Label>
                            <Input type="datetime-local" style={inputStyle} value={tempFilters.startDate} onChange={(e) => setTempFilters({ ...tempFilters, startDate: e.target.value })} />
                        </FormGroup>
                        <FormGroup>
                            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>END DATE</Label>
                            <Input type="datetime-local" style={inputStyle} value={tempFilters.endDate} onChange={(e) => setTempFilters({ ...tempFilters, endDate: e.target.value })} />
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
                    <button onClick={clearFilters} style={btnSecondary}>Clear</button>
                    <button onClick={applyFilters} style={btnPrimary}>Apply</button>
                </ModalFooter>
            </Modal>

            <OrderDetailModal
                selectedOrder={selectedOrder}
                orderModalOpen={orderModalOpen}
                setOrderModalOpen={setOrderModalOpen}
            />

            {/* Loyalty Detail Modal */}
            <Modal isOpen={loyaltyModalOpen} toggle={() => setLoyaltyModalOpen(false)}>
                <ModalHeader toggle={() => setLoyaltyModalOpen(false)}>Loyalty Program Details</ModalHeader>
                <ModalBody>
                    {selectedLoyalty && (
                        <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", rowGap: 10, columnGap: 12, fontSize: 13 }}>
                            <div style={{ fontWeight: 600, color: C.textMed }}>ID</div>
                            <div style={{ color: C.text }}>#{selectedLoyalty.id}</div>
                            <div style={{ fontWeight: 600, color: C.textMed }}>Name</div>
                            <div style={{ color: C.text, fontWeight: 600 }}>{selectedLoyalty.name}</div>
                            <div style={{ fontWeight: 600, color: C.textMed }}>Type</div>
                            <div>
                                {selectedLoyalty.loyaltyType === "stamps"
                                    ? <Badge color={C.orange} bg={C.orangeLight}>STAMPS</Badge>
                                    : <Badge color={C.blue} bg={C.blueLight}>PERCENTAGE</Badge>}
                            </div>
                            <div style={{ fontWeight: 600, color: C.textMed }}>Status</div>
                            <div>
                                {selectedLoyalty.isActive
                                    ? <Badge color={C.green} bg={C.greenLight}>● Active</Badge>
                                    : <Badge color={C.coral} bg={C.coralLight}>● Inactive</Badge>}
                            </div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter style={{ borderTop: `1px solid ${C.border}` }}>
                    <button onClick={() => setLoyaltyModalOpen(false)} style={btnSecondary}>Close</button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

export default LoyaltyLedger;
